import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  createAd,
  deleteAd,
  listAds,
  updateAd,
  updateAdMedia,
} from "../services/dataService";
import { extractErrorMessage } from "../utils/errors";
import { CARD_LABEL_COLOR } from "../styles/tokens";
import CollapsibleSection from "./settings/CollapsibleSection";
import { ACCENT, StatusText, smallButtonSx } from "./settings/SettingsControls";

const PLACEMENTS = [
  {
    value: "home_top_promo",
    label: "Метрики — баннер перед \"Сумма со скидкой\"",
  },
  {
    value: "cost_top_promo",
    label: "Себестоимость — баннер перед \"Себестоимость продаж\"",
  },
  {
    value: "events_top_promo",
    label: "События — баннер перед карточками событий",
  },
];

const initialForm = {
  id: null,
  placement: PLACEMENTS[0].value,
  title: "",
  description: "",
  redirect_url: "",
  start_at: "",
  end_at: "",
  priority: 0,
  is_active: true,
  file: null,
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const toIsoOrNull = (value) => {
  if (!value) return null;
  return new Date(value).toISOString();
};

const formatErrorMessage = (err) => {
  const status = err?.response?.status || err?.status;
  if (status === 413) {
    return "Файл слишком большой. Загрузите баннер меньшего размера.";
  }
  return extractErrorMessage(err);
};

// Раздел «Баннеры» в настройках администратора: раскрывается по нажатию, список грузится при раскрытии
const BannerAdmin = ({ token, sx }) => (
  <CollapsibleSection id="banners" title="Баннеры" sx={sx}>
    <BannerManager token={token} />
  </CollapsibleSection>
);

const BannerManager = ({ token }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [mode, setMode] = useState("create"); // create | edit

  const loadAds = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await listAds(token);
      setAds(response);
      setError(null);
    } catch (err) {
      console.error("Не удалось загрузить баннеры:", err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleOpenCreate = () => {
    setMode("create");
    setForm(initialForm);
    setDialogOpen(true);
    setError(null);
  };

  const handleOpenEdit = (ad) => {
    setMode("edit");
    setForm({
      id: ad.id,
      placement: ad.placement,
      title: ad.title || "",
      description: ad.description || "",
      redirect_url: ad.redirect_url || "",
      start_at: toInputDateTime(ad.start_at),
      end_at: toInputDateTime(ad.end_at),
      priority: ad.priority ?? 0,
      is_active: ad.is_active,
      file: null,
    });
    setDialogOpen(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setForm(initialForm);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : name === "priority" ? Number(value) : value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setForm((prev) => ({
      ...prev,
      file,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      setError("Токен авторизации не найден.");
      return;
    }

    if (mode === "create" && !form.file) {
      setError("Выберите файл баннера (jpg/png/gif/mp4).");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (mode === "create") {
        const payload = new FormData();
        payload.append("placement", form.placement);
        payload.append("title", form.title);
        payload.append("description", form.description);
        payload.append("redirect_url", form.redirect_url);
        payload.append("is_active", String(form.is_active));
        payload.append("priority", String(form.priority ?? 0));
        if (form.start_at) payload.append("start_at", toIsoOrNull(form.start_at));
        if (form.end_at) payload.append("end_at", toIsoOrNull(form.end_at));
        payload.append("file", form.file);
        await createAd(token, payload);
      } else if (mode === "edit" && form.id) {
        const updatePayload = {
          title: form.title,
          description: form.description,
          redirect_url: form.redirect_url,
          is_active: form.is_active,
          priority: form.priority ?? 0,
          start_at: toIsoOrNull(form.start_at),
          end_at: toIsoOrNull(form.end_at),
        };

        await updateAd(token, form.id, updatePayload);

        if (form.file) {
          await updateAdMedia(token, form.id, form.file);
        }
      }

      await loadAds();
      setDialogOpen(false);
      setForm(initialForm);
    } catch (err) {
      console.error("Ошибка сохранения баннера:", err);
      setError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (ad) => {
    if (!token) {
      setError("Токен авторизации не найден.");
      return;
    }
    try {
      await updateAd(token, ad.id, { is_active: !ad.is_active });
      await loadAds();
    } catch (err) {
      console.error("Ошибка при переключении статуса баннера:", err);
      setError(formatErrorMessage(err));
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Удалить баннер "${ad.title || ad.id}"?`)) return;
    if (!token) {
      setError("Токен авторизации не найден.");
      return;
    }
    try {
      await deleteAd(token, ad.id);
      await loadAds();
    } catch (err) {
      console.error("Ошибка при удалении баннера:", err);
      setError(formatErrorMessage(err));
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" mb={1}>
        <Tooltip title="Обновить список">
          <span>
            <IconButton onClick={loadAds} disabled={loading} aria-label="Обновить список" sx={{ width: 44, height: 44, color: CARD_LABEL_COLOR }}>
              {loading ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Button startIcon={<AddIcon />} onClick={handleOpenCreate} sx={smallButtonSx}>
          Добавить
        </Button>
      </Stack>

      <StatusText status={error ? { type: "error", text: error } : null} sx={{ mb: 2 }} />

      {loading && ads.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : ads.length === 0 ? (
        <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
          Баннеры не найдены. Добавьте первый баннер.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
          {ads.map((ad) => (
            <Grid item xs={12} key={ad.id}>
              <Card
                sx={{
                  background: "rgba(0,0,0,0.35)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "flex-start" }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: "#fff" }}>
                        {ad.title || "Без названия"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.5 }}>
                        Placement: {ad.placement}
                      </Typography>
                      {ad.redirect_url && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#64b5f6", mt: 0.5, wordBreak: "break-word" }}
                        >
                          {ad.redirect_url}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} mt={1.5} alignItems="center">
                        <Chip
                          label={ad.media_type === "video" ? "Видео" : "Изображение"}
                          size="small"
                          color={ad.media_type === "video" ? "primary" : "secondary"}
                        />
                        <Chip
                          label={`Приоритет ${ad.priority ?? 0}`}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: "rgba(255,255,255,0.2)", color: "#fff" }}
                        />
                        <Chip
                          label={ad.is_active ? "Активен" : "Выключен"}
                          size="small"
                          color={ad.is_active ? "success" : "default"}
                        />
                      </Stack>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        Вкл
                      </Typography>
                      <Switch
                        checked={Boolean(ad.is_active)}
                        onChange={() => handleToggleActive(ad)}
                        color="success"
                        size="small"
                      />
                      <Tooltip title="Редактировать">
                        <IconButton color="primary" onClick={() => handleOpenEdit(ad)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <IconButton color="error" onClick={() => handleDelete(ad)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>{mode === "create" ? "Новый баннер" : "Редактирование баннера"}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} mt={1}>
              <TextField
                select
                label="Плейсмент"
                name="placement"
                value={form.placement}
                onChange={handleChange}
                disabled={mode === "edit"}
              >
                {PLACEMENTS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Заголовок"
                name="title"
                value={form.title}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Описание"
                name="description"
                value={form.description}
                minRows={2}
                multiline
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Ссылка при клике"
                name="redirect_url"
                value={form.redirect_url}
                onChange={handleChange}
                fullWidth
                placeholder="https://..."
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Начало показа"
                  type="datetime-local"
                  name="start_at"
                  value={form.start_at}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Окончание показа"
                  type="datetime-local"
                  name="end_at"
                  value={form.end_at}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  label="Приоритет"
                  type="number"
                  name="priority"
                  value={form.priority}
                  onChange={handleChange}
                  fullWidth
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>Активен</Typography>
                  <Switch
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                    color="success"
                  />
                </Stack>
              </Stack>

              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                {mode === "create" ? "Загрузить медиa" : "Заменить медиа"}
                <input hidden type="file" onChange={handleFileChange} accept=".jpg,.jpeg,.png,.gif,.mp4" />
              </Button>
              {form.file && (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Выбран файл: {form.file.name}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Отмена
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} color="inherit" /> : "Сохранить"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default BannerAdmin;

